/**
 * Catálogo de cuentas.
 *
 * Deliberately small. A pulpería owner is going to read these names, so they say what they
 * mean in Spanish rather than following a formal Nicaraguan accounting catalogue.
 *
 * Several accounts exist here before the feature that uses them does. That is on purpose: IVA
 * por pagar exists although the business is not DGI-registered, and cuentas por cobrar sat
 * here unused until fiado arrived. Wiring an account in later is easy; migrating a year of
 * history into a ledger that had nowhere to put a number is not.
 */

import type { Centavos } from '@abastece/shared';

export type AccountNature = 'activo' | 'pasivo' | 'patrimonio' | 'ingreso' | 'costo' | 'gasto';
export type NormalBalance = 'debe' | 'haber';

export interface Account {
  code: string;
  name: string;
  nature: AccountNature;
  normal: NormalBalance;
  /** Shown in the simplified cash-book UI rather than only in the formal ledger. */
  hint?: string;
}

const A = (code: string, name: string, nature: AccountNature, hint?: string): Account => ({
  code,
  name,
  nature,
  normal: nature === 'activo' || nature === 'costo' || nature === 'gasto' ? 'debe' : 'haber',
  ...(hint ? { hint } : {}),
});

export const ACCOUNTS = {
  // ---------- Activo ----------
  /**
   * The till, and only the till: the cash a shift is responsible for and counts at the corte.
   * A purchase, an expense or a supplier paid out of the drawer comes from here too, recorded
   * against the open till (`sesionId`) so the corte expects that much less (see `pagoDeCaja`).
   */
  CAJA_CORDOBAS: A('1100', 'Caja (gaveta)', 'activo', 'Efectivo C$ del turno, se cuenta en el corte'),
  CAJA_DOLARES: A('1110', 'Caja dólares', 'activo', 'Efectivo US$ en la gaveta'),
  /**
   * The bank accounts (`bancos.ts` in shared, 2026-10-10): up to five in córdobas and five in
   * dollars, each its own account so each is squared with its own statement. The first of each
   * currency is the one every shop always had; the others are opened, and named, by the owner, and
   * sit here before any shop opens them, as the chart's other accounts did before their features.
   */
  BANCOS: A('1150', 'Bancos', 'activo', 'Transferencias recibidas'),
  BANCOS_2: A('1151', 'Banco 2', 'activo', 'Otra cuenta en córdobas'),
  BANCOS_3: A('1152', 'Banco 3', 'activo', 'Otra cuenta en córdobas'),
  BANCOS_4: A('1153', 'Banco 4', 'activo', 'Otra cuenta en córdobas'),
  BANCOS_5: A('1154', 'Banco 5', 'activo', 'Otra cuenta en córdobas'),
  /**
   * A dollar account: carried in córdobas at the official rate of each movement, and in dollars on
   * every line of the journal (`JournalLine.dolares`), which is what its statement is in.
   */
  BANCO_DOLARES: A('1155', 'Banco en dólares', 'activo', 'Depósitos en dólares'),
  BANCO_DOLARES_2: A('1156', 'Banco en dólares 2', 'activo', 'Otra cuenta en dólares'),
  BANCO_DOLARES_3: A('1157', 'Banco en dólares 3', 'activo', 'Otra cuenta en dólares'),
  BANCO_DOLARES_4: A('1158', 'Banco en dólares 4', 'activo', 'Otra cuenta en dólares'),
  BANCO_DOLARES_5: A('1159', 'Banco en dólares 5', 'activo', 'Otra cuenta en dólares'),
  /** All other cash: the safe, the owner's pocket, whatever is not in the drawer this shift. */
  CAJA_FUERTE: A('1120', 'Efectivo general', 'activo', 'Fuera de la gaveta: caja fuerte y pagos'),
  /**
   * Money a till handed over that nobody has counted yet: the corte's takeout or a retiro, with
   * the owner not there to take it in hand. It becomes general cash when the owner receives it,
   * counted, and what is short then is the till's.
   */
  EFECTIVO_POR_RECIBIR: A('1130', 'Efectivo por recibir', 'activo', 'Entregado por una caja, sin contar todavía'),
  BILLETERA_MOVIL: A('1160', 'Billetera móvil', 'activo', 'Tigo Money y similares'),
  INVENTARIO: A('1200', 'Inventario', 'activo', 'Mercadería en tienda y bodega'),
  INVENTARIO_CAMION: A('1210', 'Inventario en camión', 'activo', 'Carga en ruta, sin liquidar'),
  INVENTARIO_TRANSFORMACION: A('1220', 'Inventario en transformación', 'activo', 'Reempaque en proceso'),
  CUENTAS_POR_COBRAR: A('1300', 'Cuentas por cobrar', 'activo', 'Fiado: lo que deben los clientes'),
  ADELANTOS_EMPLEADOS: A('1310', 'Adelantos a empleados', 'activo', 'Salario adelantado, a descontar en la planilla'),
  TRANSFERENCIAS_PENDIENTES: A('1350', 'Transferencias sin confirmar', 'activo', 'Cobradas pero no verificadas'),
  SALDO_TELEFONICO: A('1400', 'Saldo telefónico', 'activo', 'Recargas compradas, aún no vendidas'),

  // ---------- Pasivo ----------
  CUENTAS_POR_PAGAR: A('2100', 'Cuentas por pagar', 'pasivo', 'Lo que se debe a proveedores'),
  AGUINALDO_POR_PAGAR: A('2200', 'Aguinaldo por pagar', 'pasivo', 'Décimo tercer mes acumulado'),
  VACACIONES_POR_PAGAR: A('2210', 'Vacaciones por pagar', 'pasivo', 'Vacaciones acumuladas'),
  INSS_POR_PAGAR: A('2220', 'INSS por pagar', 'pasivo', 'Laboral retenido + patronal'),
  INATEC_POR_PAGAR: A('2230', 'INATEC por pagar', 'pasivo', '2% de la planilla bruta'),
  IR_RETENIDO_POR_PAGAR: A('2240', 'IR retenido por pagar', 'pasivo', 'Retención sobre salarios'),
  /** The net wage, owed from the moment the run is closed until the money is handed over. */
  SUELDOS_POR_PAGAR: A('2250', 'Sueldos por pagar', 'pasivo', 'Neto de la planilla, aún no entregado'),
  IVA_POR_PAGAR: A('2300', 'IVA por pagar', 'pasivo', 'Inactivo hasta registrarse en la DGI'),

  // ---------- Patrimonio ----------
  CAPITAL: A('3100', 'Capital', 'patrimonio'),
  RETIROS_DUENO: A('3200', 'Retiros del dueño', 'patrimonio', 'Dinero que saca el dueño'),
  RESULTADOS_ACUMULADOS: A('3300', 'Resultados acumulados', 'patrimonio'),

  // ---------- Ingresos ----------
  VENTAS: A('4100', 'Ventas', 'ingreso'),
  COMISIONES: A('4200', 'Comisiones', 'ingreso', 'Recargas de saldo'),
  /**
   * What customers pay for having their order brought to them. Apart from ventas, as descuentos
   * are: the goods' margin stays what the goods made, and the trips are a figure of their own.
   */
  ENVIOS: A('4300', 'Cobros por envío', 'ingreso', 'Lo que pagan los clientes por llevarles el pedido'),
  GANANCIA_CAMBIARIA: A('4900', 'Ganancia cambiaria', 'ingreso', 'Diferencia contra la tasa oficial'),
  SOBRANTE_CAJA: A('4910', 'Sobrante de caja', 'ingreso'),
  SOBRANTE_INVENTARIO: A('4920', 'Sobrante de inventario', 'ingreso', 'Apareció más de lo registrado'),
  SOBRANTE_GENERAL: A('4930', 'Sobrante de efectivo general', 'ingreso', 'Apareció al contar la caja fuerte'),

  // ---------- Costos ----------
  COSTO_VENTAS: A('5100', 'Costo de ventas', 'costo'),
  DESCUENTOS: A('5200', 'Descuentos concedidos', 'costo', 'Rebajas dadas sobre el precio de lista'),
  /**
   * Beside descuentos, and for the same reason: sales stays what was sold, and what came back
   * is a figure of its own that a shop can watch — one that jumps is a product, or a cashier,
   * worth asking about.
   */
  DEVOLUCIONES_VENTAS: A('5210', 'Devoluciones de clientes', 'costo', 'Lo que los clientes devolvieron'),
  /**
   * What the stock's worth changed by when the owner set a product's cost right
   * (`inventario.costo_corregido`, 2026-10-13). A cost, beside cost of sales: what the shelf was
   * carried at too high would have gone there as it sold, and a count that valued goods at the wrong
   * cost put its difference in «Sobrante de inventario», which is in the same gross profit.
   */
  AJUSTE_COSTO_INVENTARIO: A('5300', 'Ajuste del costo de inventario', 'costo', 'El dueño corrigió lo que cuesta la mercadería'),

  // ---------- Gastos ----------
  SUELDOS: A('6100', 'Sueldos y salarios', 'gasto'),
  INSS_PATRONAL: A('6110', 'INSS patronal', 'gasto', '21.5% sobre la planilla'),
  INATEC_GASTO: A('6120', 'INATEC', 'gasto', '2% sobre la planilla'),
  AGUINALDO_GASTO: A('6130', 'Aguinaldo', 'gasto'),
  VACACIONES_GASTO: A('6140', 'Vacaciones', 'gasto'),
  /** Exempt from INSS and IR (Art. 45 CT), and an expense the month someone leaves. */
  INDEMNIZACION_GASTO: A('6150', 'Indemnización', 'gasto', 'Antigüedad al terminar el contrato'),
  MERMA: A('6200', 'Merma y daño', 'gasto', 'Producto dañado o perdido'),
  VENCIDOS: A('6210', 'Producto vencido', 'gasto'),
  CONSUMO_INTERNO: A('6220', 'Consumo interno', 'gasto', 'Lo que se consume en la casa'),
  ALQUILER: A('6300', 'Alquiler', 'gasto'),
  ENERGIA: A('6310', 'Energía eléctrica', 'gasto'),
  AGUA: A('6320', 'Agua', 'gasto'),
  TRANSPORTE: A('6330', 'Transporte y combustible', 'gasto'),
  TELEFONO_INTERNET: A('6340', 'Teléfono e internet', 'gasto'),
  MANTENIMIENTO: A('6350', 'Mantenimiento y reparaciones', 'gasto'),
  /** Matrícula y basura de la alcaldía. Municipal, not DGI: the business is not registered. */
  IMPUESTOS_MUNICIPALES: A('6360', 'Impuestos municipales', 'gasto', 'Matrícula y basura de la alcaldía'),
  EMPAQUES: A('6370', 'Empaques y bolsas', 'gasto'),
  OTROS_GASTOS: A('6390', 'Otros gastos', 'gasto'),
  FALTANTE_CAJA: A('6400', 'Faltante de caja', 'gasto'),
  /**
   * Kept apart from merma on purpose. Damaged product and product that simply is not there are
   * different facts, and a shop that cannot tell them apart cannot tell spoilage from theft.
   */
  FALTANTE_INVENTARIO: A('6230', 'Faltante de inventario', 'gasto', 'Producto que no aparece'),
  /** Kept apart from shop shortfalls: this one has a name attached to it. */
  FALTANTE_REPARTO: A('6240', 'Faltante de reparto', 'gasto', 'Mercadería que no volvió del camión'),
  REDONDEO: A('6410', 'Redondeo de efectivo', 'gasto', 'Centavos que no circulan'),
  PERDIDA_CAMBIARIA: A('6900', 'Pérdida cambiaria', 'gasto'),
  TRANSFERENCIAS_PERDIDAS: A('6420', 'Transferencias no cobradas', 'gasto', 'El dinero nunca llegó'),
  /**
   * Kept apart from the till's: general cash is nobody's shift, so a shortfall here has no
   * cashier's name on it, and mixing the two would hide which one is growing.
   */
  FALTANTE_GENERAL: A('6430', 'Faltante de efectivo general', 'gasto', 'Faltó al contar la caja fuerte'),
  /** What the bank or the wallet says, against the books: fees, and money that never came. */
  DIFERENCIAS_CONCILIACION: A('6440', 'Diferencias de conciliación', 'gasto', 'Banco o billetera contra su estado de cuenta'),
  /**
   * Fiado the shop will not collect, given up on by the owner (`incobrable.registrado`). Its own
   * account, so what fiado costs the shop is a figure it can watch, and not an abono that never
   * happened or a debt that stays in «Cuentas por cobrar» for ever.
   */
  FIADO_INCOBRABLE: A('6450', 'Fiado incobrable', 'gasto', 'Lo que un cliente debía y no se va a cobrar'),
} as const satisfies Record<string, Account>;

export type AccountKey = keyof typeof ACCOUNTS;
export const ACCOUNT_LIST: readonly Account[] = Object.values(ACCOUNTS);

const BY_CODE = new Map(ACCOUNT_LIST.map((a) => [a.code, a]));
export function accountByCode(code: string): Account | undefined {
  return BY_CODE.get(code);
}

/** Does a positive movement on this account increase it by a debit or a credit? */
export function signFor(account: Account, side: 'debe' | 'haber'): 1 | -1 {
  return account.normal === side ? 1 : -1;
}

export interface AccountBalance {
  code: string;
  debe: Centavos;
  haber: Centavos;
  /** Signed by the account's normal balance, so a positive number always means "more of this". */
  saldo: Centavos;
  /** A dollar account's dollars, USD cents (`JournalLine.dolares`). */
  dolares?: Centavos;
}

/** The dollar accounts' codes: the only ones whose lines carry dollars (`JournalLine.dolares`). */
const EN_DOLARES = new Set(
  [ACCOUNTS.BANCO_DOLARES, ACCOUNTS.BANCO_DOLARES_2, ACCOUNTS.BANCO_DOLARES_3, ACCOUNTS.BANCO_DOLARES_4, ACCOUNTS.BANCO_DOLARES_5].map(
    (a) => a.code,
  ),
);
export const esCodigoEnDolares = (code: string): boolean => EN_DOLARES.has(code);
